// src/hooks/useJobChecklists.js
// chklstweb-3a7f1c58 — CHECKLISTS SEAM 3 (web).
//
// Live view of one job's checklists while the pop-up is open:
//
//     organizations/{orgId}/jobs/{jobId}/checklists        the copies + answers
//     organizations/{orgId}/jobChecklistState/{jobId}      gate, override, unlock
//
// Both are server-write-only (Seam 1, chklst-7e4b2a96) and staff-only — no portal
// clause in firestore.rules. Never reuse this hook in a portal-facing view.
//
// Soft-removed copies (removed: true) are filtered out here: they keep their answers
// on the server so attaching the template again restores them, but they are not on
// the job. Sorted by when they were added (oldest first), so rows don't jump.
//
// `enabled` lets the always-mounted JobQuickViewModal call this unconditionally and
// still unsubscribe the moment the pop-up closes.

import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';

const addedAtMs = (c) => (c.addedAt?.toMillis ? c.addedAt.toMillis() : 0);

const useJobChecklists = ({ orgId, jobId, enabled = true }) => {
    const [checklists, setChecklists] = useState([]);
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!enabled || !orgId || !jobId) {
            setChecklists([]);
            setState(null);
            setLoading(false);
            return undefined;
        }
        setLoading(true);
        const unsubList = onSnapshot(
            collection(db, `organizations/${orgId}/jobs/${jobId}/checklists`),
            (snap) => {
                setChecklists(snap.docs
                    .map(d => ({ id: d.id, ...d.data() }))
                    .filter(c => c.removed !== true)
                    .sort((a, b) => addedAtMs(a) - addedAtMs(b)));
                setLoading(false);
            },
            (err) => {
                console.error('useJobChecklists: checklists listener failed', err);
                setChecklists([]);
                setLoading(false);
            },
        );
        const unsubState = onSnapshot(
            doc(db, `organizations/${orgId}/jobChecklistState/${jobId}`),
            (snap) => setState(snap.exists() ? snap.data() : null),
            (err) => {
                console.error('useJobChecklists: state listener failed', err);
                setState(null);
            },
        );
        return () => { unsubList(); unsubState(); };
    }, [orgId, jobId, enabled]);

    return { checklists, state, loading };
};

export default useJobChecklists;
